import type { DefaultError, QueryKey } from '@tanstack/query-core';
import {
  useQuery,
  UseQueryOptions,
  DefinedUseQueryResult,
  UseQueryResult,
  DefinedInitialDataOptions,
  UndefinedInitialDataOptions,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
  useSuspenseQuery,
} from '@tanstack/react-query';

import { getPersistedQueryClient } from 'common/query/queryClients';

export function usePersistedApiQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError>;

export function usePersistedApiQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
): DefinedUseQueryResult<TData, TError>;

export function usePersistedApiQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<TData, TError>;

export function usePersistedApiQuery(options: UseQueryOptions) {
  return useQuery(options, getPersistedQueryClient());
}

export function usePersistedApiSuspenseQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseSuspenseQueryResult<TData, TError>;

export function usePersistedApiSuspenseQuery(options: UseSuspenseQueryOptions) {
  return useSuspenseQuery(options, getPersistedQueryClient());
}
